/**
 * Shared pack-details form used by the Squad / Officials / Venue wizards.
 * Sits in the WizardShell sidebar. Fields: name, season, price, short
 * description, long description. Also renders the test-mode toggle at
 * the bottom.
 *
 * Props:
 *   - details : { name, season, short_description, description, price_credits }
 *   - setDetails : dispatcher
 *   - namePlaceholder / shortDescriptionPlaceholder : per-wizard hints
 *   - testMode / setTestMode : the "skip promotion" toggle state
 *                              (omit both to hide the toggle — child pack
 *                              wizards do this since Publish is now owned
 *                              exclusively by TOURNAMENT_BUNDLE).
 */
export default function PackDetailsForm({
  details,
  setDetails,
  namePlaceholder = "",
  shortDescriptionPlaceholder = "",
  testMode,
  setTestMode,
}) {
  const showTestMode = typeof setTestMode === "function";
  return (
    <div className="space-y-4">
      <h3 className="text-sm font-semibold uppercase tracking-widest text-text-secondary">
        Pack details
      </h3>

      <label className="block">
        <span className="text-xs text-text-secondary">Name *</span>
        <input
          value={details.name}
          onChange={(e) => setDetails((d) => ({ ...d, name: e.target.value }))}
          placeholder={namePlaceholder}
          className="mt-1 w-full px-3 py-2 text-sm rounded-md border border-border-default bg-bg-primary focus:outline-none focus:border-button-primary"
        />
      </label>

      <div className="grid grid-cols-2 gap-3">
        <label className="block">
          <span className="text-xs text-text-secondary">Season</span>
          <input
            value={details.season}
            onChange={(e) => setDetails((d) => ({ ...d, season: e.target.value }))}
            placeholder="2026"
            className="mt-1 w-full px-3 py-2 text-sm rounded-md border border-border-default bg-bg-primary focus:outline-none focus:border-button-primary"
          />
        </label>
        <label className="block">
          <span className="text-xs text-text-secondary">Price (ZC) *</span>
          <input
            type="number"
            min={0}
            value={details.price_credits}
            onChange={(e) => setDetails((d) => ({ ...d, price_credits: e.target.value }))}
            className="mt-1 w-full px-3 py-2 text-sm rounded-md border border-border-default bg-bg-primary focus:outline-none focus:border-button-primary"
          />
        </label>
      </div>

      <label className="block">
        <span className="text-xs text-text-secondary">Short description (tile blurb)</span>
        <input
          value={details.short_description}
          onChange={(e) => setDetails((d) => ({ ...d, short_description: e.target.value }))}
          placeholder={shortDescriptionPlaceholder}
          className="mt-1 w-full px-3 py-2 text-sm rounded-md border border-border-default bg-bg-primary focus:outline-none focus:border-button-primary"
        />
      </label>

      <label className="block">
        <span className="text-xs text-text-secondary">Long description</span>
        <textarea
          value={details.description}
          onChange={(e) => setDetails((d) => ({ ...d, description: e.target.value }))}
          rows={4}
          placeholder="What's inside, who it's for, any usage notes…"
          className="mt-1 w-full px-3 py-2 text-sm rounded-md border border-border-default bg-bg-primary focus:outline-none focus:border-button-primary"
        />
      </label>

      {showTestMode && (
        <div className="pt-3 border-t border-border-default">
          <label className="flex items-start gap-2 text-xs text-text-secondary cursor-pointer">
            <input
              type="checkbox"
              checked={testMode}
              onChange={(e) => setTestMode(e.target.checked)}
              className="accent-button-primary mt-0.5"
            />
            <span>
              <span className="font-medium text-text-primary block mb-0.5">Test mode</span>
              Skip promotion fan-out (WhatsApp + email + in-app). Recommended
              on first publish; turn OFF only when you're ready to notify every
              B2C subscriber.
            </span>
          </label>
        </div>
      )}
    </div>
  );
}
